import Button from "../../component/button/button"
import CustomInput from "../../component/CustomInput/CustomInput"
import CustomSelect from "../../component/customSelect/customSelect"
import { FiCheck } from "react-icons/fi"
import { useCallback, useEffect, useMemo, useState } from "react"
import { listSubject } from "src/api/subject"
import { QuestionListFilter } from "../../api/type"
import { GRADES } from "../../utils/grade"
import { SubjectListOption } from "../../types/subjectSelect"
import LevelSelectors, {
  LevelFieldValues,
} from "./components/LevelSelectors"

type IFIlterProps = {
  filter: QuestionListFilter
  setFilter: React.Dispatch<React.SetStateAction<QuestionListFilter>>
  applyFilter: () => void
  clearFilter: () => void
}

export type ISubjects = SubjectListOption & {
  _id?: string
  level_labels?: string[]
}

const CONTENT_TYPE_OPTIONS = [
  { label: "All types", value: "" },
  { label: "Text", value: "text" },
  { label: "Math", value: "math" },
  { label: "Image", value: "image" },
  { label: "Graph", value: "graph" },
  { label: "Geometry", value: "geometry" },
]

const LEVEL_OPTIONS = [{ label: "All levels", value: "" }, ...GRADES]

const getFilterSelectValue = (
  filterValue: string,
  allLabel: string,
  options: { label: string; value: string }[],
) => {
  if (!filterValue) {
    return { label: allLabel, value: "" }
  }

  return (
    options.find((item) => item.value === filterValue) ?? {
      label: filterValue,
      value: filterValue,
    }
  )
}

const SORT_OPTIONS = [
  { label: "Newest", value: "newest" },
  { label: "Oldest", value: "oldest" },
  { label: "Year", value: "year" },
  { label: "Difficulty", value: "difficulty" },
  { label: "Level", value: "level" },
  { label: "Marks", value: "marks" },
  { label: "Topic", value: "topic" },
]

const STATUS_OPTIONS = [
  { label: "All status", value: "" },
  { label: "Published", value: "published" },
  { label: "Draft", value: "draft" },
  { label: "Archived", value: "archived" },
]

const LEARNER_OPTIONS = [
  { label: "All", value: "" },
  { label: "Live to learners", value: "true" },
  { label: "Not live", value: "false" },
]

const VERIFICATION_OPTIONS = [
  { label: "All", value: "" },
  { label: "Not verified", value: "unverified" },
  { label: "Verified", value: "verified" },
  { label: "Rejected", value: "rejected" },
]

const difficult_level = [
  { name: "All levels", value: "" },
  { name: "Hard", value: "hard" },
  { name: "Medium", value: "medium" },
  { name: "Easy", value: "easy" },
]

const Filter = ({
  filter,
  setFilter,
  applyFilter,
  clearFilter,
}: IFIlterProps) => {
  const [subjects, setSubjects] = useState<ISubjects[]>([])

  const fetchSubjects = useCallback(async () => {
    try {
      const res = await listSubject(1, "", 100000)
      const apiSubjects = res.data?.data?.subjects ?? []
      const tempSub = apiSubjects.map((s: any) => ({
        _id: s.id,
        label: s.title || "",
        level_labels: Array.isArray(s.level_labels) ? s.level_labels : undefined,
      }))
      setSubjects(tempSub)
    } catch (error) {
      console.error("Failed to fetch subjects:", error)
      setSubjects([])
    }
  }, [])

  useEffect(() => {
    void fetchSubjects()
  }, [fetchSubjects])

  const clearLevels = (prev: QuestionListFilter): QuestionListFilter => ({
    ...prev,
    topic: "",
    chapter: "",
    level_1: "",
    level_2: "",
    level_3: "",
    level_4: "",
  })

  const handleSubjectSelect = (subjectId: string) => {
    setFilter((prev) => ({ ...clearLevels(prev), subject: subjectId }))
  }

  const handleAllSubjects = () => {
    setFilter((prev) => ({ ...clearLevels(prev), subject: "" }))
  }

  const selectedSubject = useMemo(
    () => subjects.find((s) => s._id === filter.subject) ?? null,
    [subjects, filter.subject],
  )

  const levelValues: LevelFieldValues = {
    level_1: filter.level_1,
    level_2: filter.level_2,
    level_3: filter.level_3,
    level_4: filter.level_4,
  }

  return (
    <div className='filter'>
      <div className='filter__scroll'>
        <div className='each_filter keyword'>
          <div className='filter_title'>
            <h3>Keyword</h3>
          </div>
          <CustomInput
            name='keyword'
            placeholder='Search question, answer, solution'
            value={filter.keyword}
            onChange={(event) =>
              setFilter((prev) => ({ ...prev, keyword: event.target.value }))
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                applyFilter()
              }
            }}
          />
        </div>

        <div className='each_filter subject'>
          <div className='filter_title'>
            <h3>Subject</h3>
          </div>

          <div className='filter_items'>
            <div
              onClick={handleAllSubjects}
              className={`filter_item  ${"" === filter?.subject && "active"}`}
            >
              All Subject
              {"" === filter?.subject && <FiCheck className='check_icon' />}
            </div>

            {subjects.map((item) => {
              return (
                <div
                  key={item?._id}
                  onClick={() => handleSubjectSelect(item?._id || "")}
                  className={`filter_item  ${item._id === filter?.subject && "active"}`}
                >
                  {item?.label}

                  {item._id === filter?.subject && (
                    <FiCheck className='check_icon' />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {filter.subject ? (
          <div className='each_filter hierarchy_levels'>
            <LevelSelectors
              subjectId={filter.subject}
              levelLabels={selectedSubject?.level_labels}
              values={levelValues}
              onChange={(next) =>
                setFilter((prev) => ({
                  ...prev,
                  level_1: next.level_1,
                  level_2: next.level_2,
                  level_3: next.level_3,
                  level_4: next.level_4,
                }))
              }
              className='hierarchy_level_field'
            />
          </div>
        ) : (
          <div className='each_filter topic'>
            <p className='filter_hint'>
              Select a subject to filter by its hierarchy.
            </p>
          </div>
        )}

        <div className='each_filter topic'>
          <div className='filter_title'>
            <h3>Level</h3>
          </div>
          <CustomSelect
            data={LEVEL_OPTIONS}
            onChange={(value: any) =>
              setFilter((prev) => ({ ...prev, grade: value?.value || "" }))
            }
            value={getFilterSelectValue(
              filter.grade,
              "All levels",
              LEVEL_OPTIONS,
            )}
          />
        </div>

        <div className='each_filter topic'>
          <div className='filter_title'>
            <h3>Year</h3>
          </div>
          <CustomInput
            name='year'
            placeholder='e.g. 2024'
            value={filter.year}
            onChange={(event) =>
              setFilter((prev) => ({ ...prev, year: event.target.value }))
            }
          />
        </div>

        <div className='each_filter topic'>
          <div className='filter_title'>
            <h3>Content Type</h3>
          </div>
          <CustomSelect
            data={CONTENT_TYPE_OPTIONS}
            onChange={(value: any) =>
              setFilter((prev) => ({
                ...prev,
                content_type: value?.value || "",
              }))
            }
            value={getFilterSelectValue(
              filter.content_type,
              "All types",
              CONTENT_TYPE_OPTIONS,
            )}
          />
        </div>

        <div className='each_filter topic'>
          <div className='filter_title'>
            <h3>Status</h3>
          </div>
          <CustomSelect
            data={STATUS_OPTIONS}
            onChange={(value: any) =>
              setFilter((prev) => ({
                ...prev,
                question_status: value?.value || "",
              }))
            }
            value={getFilterSelectValue(
              filter.question_status,
              "All status",
              STATUS_OPTIONS,
            )}
          />
        </div>

        <div className='each_filter topic'>
          <div className='filter_title'>
            <h3>Learner visibility</h3>
          </div>
          <CustomSelect
            data={LEARNER_OPTIONS}
            onChange={(value: any) =>
              setFilter((prev) => ({
                ...prev,
                published_to_learner: value?.value || "",
              }))
            }
            value={getFilterSelectValue(
              filter.published_to_learner,
              "All",
              LEARNER_OPTIONS,
            )}
          />
        </div>

        <div className='each_filter topic'>
          <div className='filter_title'>
            <h3>Verification</h3>
          </div>
          <CustomSelect
            data={VERIFICATION_OPTIONS}
            onChange={(value: any) =>
              setFilter((prev) => ({
                ...prev,
                verification_status: value?.value || "",
              }))
            }
            value={getFilterSelectValue(
              filter.verification_status,
              "All",
              VERIFICATION_OPTIONS,
            )}
          />
        </div>

        <div className='each_filter topic'>
          <div className='filter_title'>
            <h3>Sort by</h3>
          </div>
          <CustomSelect
            data={SORT_OPTIONS}
            onChange={(value: any) =>
              setFilter((prev) => ({
                ...prev,
                sort_by: value?.value || "newest",
              }))
            }
            value={
              SORT_OPTIONS.find((item) => item.value === filter.sort_by) ||
              SORT_OPTIONS[0]
            }
          />
        </div>

        <div className='each_filter difficulty'>
          <div className='filter_title'>
            <h3>Difficulty Level</h3>
          </div>
          <div className='difficulty_container'>
            {difficult_level.map((item) => {
              return (
                <div
                  onClick={() =>
                    setFilter((prev) => ({
                      ...prev,
                      difficultyLevel: item?.value,
                    }))
                  }
                  key={item?.name}
                  className={`single_difficult  ${filter.difficultyLevel === item.value && "active"} `}
                >
                  <div className='radio_box'>
                    <div className='dot'></div>
                  </div>
                  {item?.name}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <div className='filter__footer'>
        <Button
          text='Reset'
          type='link'
          onClick={clearFilter}
          style={{
            color: "var(--gray-600)",
          }}
        />
        <Button
          text='Apply Filter'
          style={{
            color: "var(--gray-600)",
            border: "1px solid var(--gray-50)",
            background: "var(--gray-50)",
            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
            fontSize: 14,
          }}
          onClick={applyFilter}
        />
      </div>
    </div>
  )
}

export default Filter
